import { HealthBar } from "./healthBar";
import { SCREEN_W } from "../shared/screen";
import { INTRO_FRAMES, PHASE, type MatchRules, type MatchState, LAST_CHANCE_PERCENT } from "../sim";
import { FULL_FLASH_FRAMES, MeterDisplay, SEGMENT, SEGMENTS, SPEND_FRAMES } from "./meterDisplay";

/** HUD в полном разрешении 1920×1080: гладкий шрифт с обводкой, не пиксельный (Джон, 04.11.2026). */
const FONT = '"DejaVu Sans", "Noto Sans", Arial, sans-serif';
const BAR_W = 780;
const BAR_H = 34;
const BAR_Y = 48;
const MARGIN = 60;

export function outlinedText(
  context: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  size: number,
  color: string,
  align: CanvasTextAlign = "center",
  weight = "bold",
): void {
  context.font = `${weight} ${size}px ${FONT}`;
  context.textAlign = align;
  context.textBaseline = "top";
  context.lineJoin = "round";
  context.lineWidth = Math.max(4, Math.round(size / 7));
  context.strokeStyle = "#0b0910";
  context.strokeText(text, x, y);
  context.fillStyle = color;
  context.fillText(text, x, y);
}

const METER_W = 128;
const METER_H = 26;
const METER_GAP = 14;
const METER_Y = 1012;

export class Hud {
  /** Полосы жизни с откликом на удар (render/healthBar.ts). */
  private readonly bars = [new HealthBar(), new HealthBar()] as const;
  /** Что показывает шкала Ярости — отдельный модуль без холста, с тестом. */
  private readonly meterView = new MeterDisplay();

  draw(
    context: CanvasRenderingContext2D,
    state: MatchState,
    rules: MatchRules,
    names: readonly [string, string],
    banner: readonly string[],
    /** Решающий удар ещё в кадре (замедление или крупный план): «НОКАУТ» ждёт, пока камера отъедет. */
    holdStamp = false,
  ): void {
    this.meterView.advance(state);
    state.fighters.forEach((fighter, side) => {
      const full = rules.heroes[side as 0 | 1].health || 1;
      const health = fighter.health / full;
      const x = side === 0 ? MARGIN : SCREEN_W - MARGIN - BAR_W;
      const low = fighter.health > 0 && fighter.health * 100 <= full * LAST_CHANCE_PERCENT;
      this.bars[side as 0 | 1].draw(
        context,
        { x, y: BAR_Y, width: BAR_W, height: BAR_H, mirrored: side === 1 },
        health,
        fighter.grey / full,
        low,
        state.frame,
      );
      if (low)
        outlinedText(
          context,
          "ПОСЛЕДНИЙ ШАНС — Ярость быстрее",
          side === 0 ? x : x + BAR_W,
          BAR_Y + BAR_H + 56,
          24,
          "#ffcf4a",
          side === 0 ? "left" : "right",
          "normal",
        );
      outlinedText(
        context,
        names[side] ?? "",
        side === 0 ? x : x + BAR_W,
        BAR_Y + BAR_H + 10,
        36,
        "#f0eaf6",
        side === 0 ? "left" : "right",
      );
      // Победы в раундах — звёзды у края полосы, ближе к таймеру: золотая — раунд выигран
      for (let win = 0; win < rules.roundsToWin; win++) {
        const won = win < fighter.wins;
        const star = side === 0 ? x + BAR_W - 22 - win * 46 : x + 22 + win * 46;
        outlinedText(context, "★", star, BAR_Y + BAR_H + 6, 40, won ? "#ffcf4a" : "#3e3038");
      }
      this.meter(context, side, fighter.meter);
    });
    outlinedText(context, String(Math.ceil(state.timer / 60)), SCREEN_W / 2, 30, 72, "#ffe2b8");
    const lines = banner.length > 0 ? banner : this.phaseLines(state, holdStamp);
    lines.forEach((line, index) => {
      if (index === 0) outlinedText(context, line, SCREEN_W / 2, 340, 132, "#ffe2b8");
      else outlinedText(context, line, SCREEN_W / 2, 360 + index * 70, 44, "#f0eaf6", "center", "normal");
    });
  }

  /** Кадр мира, когда начался бой раунда: «БОЙ!» горит 30 кадров мира от него — заставка его не продлевает. */
  private fightStart = -1;
  private lastPhase = -1;

  private phaseLines(state: MatchState, holdStamp: boolean): string[] {
    if (state.phase !== this.lastPhase) {
      // начало боя — по миру, а не по первому нарисованному кадру: после пропуска кадров надпись не залипает
      if (state.phase === PHASE.fight) this.fightStart = state.frame - state.phaseFrame;
      this.lastPhase = state.phase;
    }
    if (state.cinematic >= 0) return []; // заставка суперприёма — своя сцена, надписей раунда нет
    if (state.phase === PHASE.intro) return state.phaseFrame < INTRO_FRAMES - 30 ? [`РАУНД ${state.round}`] : ["БОЙ!"];
    if (state.phase === PHASE.fight && state.frame - this.fightStart < 30) return ["БОЙ!"];
    // «НОКАУТ» — после падения, когда камера отъехала (консультант 05.11.2026: «ложится поверх головы отлетающего и
    // закрывает сам удар… как в Tekken»)
    if (state.phase === PHASE.ko) return holdStamp ? [] : state.winner < 0 ? ["НИЧЬЯ"] : ["НОКАУТ"];
    if (state.phase === PHASE.timeUp) return ["ВРЕМЯ"];
    if (state.phase === PHASE.matchEnd)
      return [`ПОБЕДА: ИГРОК ${state.fighters[0].wins > state.fighters[1].wins ? 1 : 2}`, "Реванш — ✕ или Enter"];
    return [];
  }

  /**
   * Шкала Ярости: подпись, три деления. Копится плавно; готовое деление горит и дышит светом; полная шкала —
   * вспышка; трата — деление вспыхивает белым и гаснет с искрами.
   */
  private meter(context: CanvasRenderingContext2D, side: number, meter: number): void {
    const mirrored = side === 1;
    const total = SEGMENTS * METER_W + (SEGMENTS - 1) * METER_GAP;
    // место под подпись «ЯРОСТЬ» — у внешнего края с обеих сторон, внутри кадра
    const left = mirrored ? SCREEN_W - MARGIN - 170 - total : MARGIN + 170;
    const label = mirrored ? left - 20 : left - 20;
    outlinedText(
      context,
      "ЯРОСТЬ",
      mirrored ? left + total + 20 : label,
      METER_Y - 6,
      32,
      "#ffcf4a",
      mirrored ? "left" : "right",
    );
    const shown = this.meterView.shown[side] ?? meter;
    const pulse = 0.55 + 0.45 * Math.sin(this.meterView.tick / 9);
    const full = this.meterView.fullFlash[side] ?? 0;
    for (let segment = 0; segment < SEGMENTS; segment++) {
      const index = mirrored ? SEGMENTS - 1 - segment : segment;
      const sx = left + index * (METER_W + METER_GAP);
      const filled = Math.max(0, Math.min(1, (shown - segment * SEGMENT) / SEGMENT));
      const ready = meter >= (segment + 1) * SEGMENT;
      context.fillStyle = "#0b0910";
      context.fillRect(sx - 4, METER_Y - 4, METER_W + 8, METER_H + 8);
      context.fillStyle = "#2a1a10";
      context.fillRect(sx, METER_Y, METER_W, METER_H);
      context.save();
      if (ready) {
        context.shadowColor = "#ffcf4a";
        context.shadowBlur = 14 + 16 * pulse + full;
      }
      context.fillStyle = ready ? "#ffd84a" : "#9a6e22";
      const width = Math.round(METER_W * filled);
      context.fillRect(mirrored ? sx + METER_W - width : sx, METER_Y, width, METER_H);
      context.restore();
      if (ready) {
        context.fillStyle = `rgba(255, 250, 220, ${0.25 + 0.35 * pulse})`;
        context.fillRect(sx, METER_Y, METER_W, 6);
      }
    }
    if (meter >= SEGMENT * SEGMENTS) {
      const hint = "L2 + R2  ·  клавиши L + P — СУПЕРПРИЁМ";
      outlinedText(
        context,
        hint,
        mirrored ? left + total : left,
        METER_Y - 44,
        24,
        pulse > 0.5 ? "#fff0b0" : "#ffcf4a",
        mirrored ? "right" : "left",
      );
    }
    if (full > 0) {
      const k = full / FULL_FLASH_FRAMES;
      context.save();
      context.strokeStyle = `rgba(255, 240, 180, ${k})`;
      context.lineWidth = 6;
      context.shadowColor = "#ffcf4a";
      context.shadowBlur = 40 * k;
      const grow = (1 - k) * 24;
      context.strokeRect(left - 10 - grow, METER_Y - 10 - grow, total + 20 + grow * 2, METER_H + 20 + grow * 2);
      context.restore();
    }
    for (const spend of this.meterView.spends) {
      if (spend.side !== side) continue;
      const index = mirrored ? SEGMENTS - 1 - spend.segment : spend.segment;
      const sx = left + index * (METER_W + METER_GAP);
      const k = spend.frames / SPEND_FRAMES;
      context.fillStyle = `rgba(255, 255, 255, ${k * 0.9})`;
      context.fillRect(sx, METER_Y, METER_W, METER_H);
      context.strokeStyle = `rgba(255, 200, 90, ${k})`;
      context.lineWidth = 3;
      for (let ray = 0; ray < 6; ray++) {
        const angle = (ray / 6) * Math.PI * 2 + spend.segment;
        const reach = 30 + (1 - k) * 60;
        const cx = sx + METER_W / 2;
        const cy = METER_Y + METER_H / 2;
        context.beginPath();
        context.moveTo(cx + Math.cos(angle) * reach * 0.6, cy + Math.sin(angle) * reach * 0.4);
        context.lineTo(cx + Math.cos(angle) * reach, cy + Math.sin(angle) * reach * 0.7);
        context.stroke();
      }
    }
  }

  reset(): void {
    for (const bar of this.bars) bar.reset();
    this.meterView.reset();
  }
}
